import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { CommerceOrderSelectorComponent } from '../../shared/design-system/commerce-order-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { MarketplaceSellerSelectorComponent } from '../../shared/design-system/marketplace-seller-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogService,
  MarketplaceDashboard,
  MarketplaceDispute,
  MarketplaceSettlementSummary
} from './catalog.service';
import {
  MARKETPLACE_LIST_TOOLBAR_STYLES,
  MarketplaceLiveSubscription,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeMarketplaceOperationsLiveUpdates,
  toggleSortField,
  unsubscribeMarketplaceOperationsLiveUpdates
} from './marketplace-list-page.utils';

type SettlementSortField = 'orderReferenceId' | 'netPayoutAmount' | 'currencyCode' | 'createdAtUtc';
type DisputeSortField = 'orderReferenceId' | 'status' | 'disputedAmount' | 'reason';

@Component({
  standalone: true,
  selector: 'ain-marketplace-dashboard',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DatePipe,
    DecimalPipe,
    DialogModule,
    FormsModule,
    InputTextModule,
    TextareaModule,
    CheckboxModule,
    SelectModule,
    CommerceOrderSelectorComponent,
    CustomerSelectorComponent,
    MarketplaceSellerSelectorComponent
  ],
  template: `
    <ain-page [title]="t('catalog.marketplace.dashboard.title')" [description]="t('catalog.marketplace.dashboard.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.help.list.title'"
        [descriptionKey]="'catalog.marketplace.help.list.description'"
        [bulletKeys]="[
          'catalog.marketplace.help.list.bullets.selection',
          'catalog.marketplace.help.list.bullets.filters',
          'catalog.marketplace.help.list.bullets.live'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.marketplace.dashboard.sellers')" severity="secondary" (click)="goSellers()"></button>
            <button pButton type="button" [label]="t('catalog.marketplace.commissionRules.title')" severity="secondary" (click)="goCommissionRules()"></button>
            <button pButton type="button" [label]="t('catalog.marketplace.sellerTiers.title')" severity="secondary" (click)="goSellerTiers()"></button>
            <button pButton type="button" [label]="t('catalog.marketplace.retailMedia.title')" severity="secondary" (click)="goRetailMedia()"></button>
            <button pButton type="button" [label]="t('catalog.marketplace.ops.escrowHold')" severity="secondary" (click)="openEscrowDialog()"></button>
            <button pButton type="button" [label]="t('catalog.marketplace.ops.settle')" severity="secondary" (click)="openSettleDialog()"></button>
            <button pButton type="button" [label]="t('catalog.marketplace.ops.openDispute')" severity="secondary" (click)="openDisputeDialog()"></button>
            <button pButton type="button" [label]="t('catalog.marketplace.actions.refresh')" severity="secondary" (click)="load()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <div class="stats">
            <div class="stat"><span class="label">{{ t('catalog.marketplace.dashboard.approvedSellers') }}</span><span class="value">{{ dashboard()?.approvedSellerCount ?? 0 }}</span></div>
            <div class="stat"><span class="label">{{ t('catalog.marketplace.dashboard.pendingSellers') }}</span><span class="value">{{ dashboard()?.pendingSellerCount ?? 0 }}</span></div>
            <div class="stat"><span class="label">{{ t('catalog.marketplace.dashboard.openDisputes') }}</span><span class="value">{{ dashboard()?.openDisputeCount ?? 0 }}</span></div>
            <div class="stat"><span class="label">{{ t('catalog.marketplace.dashboard.heldEscrow') }}</span><span class="value">{{ dashboard()?.heldEscrowCount ?? 0 }}</span></div>
            <div class="stat"><span class="label">{{ t('catalog.marketplace.dashboard.settledNet') }}</span><span class="value">{{ dashboard()?.totalSettledNetPayout | number:'1.2-2' }}</span></div>
          </div>

          <section class="card list-panel">
            <h3>{{ t('catalog.marketplace.dashboard.recentSettlements') }}</h3>
            <div class="toolbar">
              <button pButton type="button" [label]="t('catalog.marketplace.ops.settle')" (click)="openSettleDialog()"></button>
              <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.refresh')" (click)="load()"></button>
              <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.bulk')" (click)="applySettlementBulk()"></button>
              <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.export')" (click)="exportSettlements()"></button>
              <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.open')" [disabled]="selectedSettlements().length !== 1" (click)="openSelectedSettlement()"></button>
            </div>
            <details class="filters" [open]="true">
              <summary>
                <span>{{ t('catalog.marketplace.filters.title') }}</span>
                <ain-info-hint
                  [titleKey]="'catalog.marketplace.help.filters.title'"
                  [descriptionKey]="'catalog.marketplace.help.filters.description'"
                  [bulletKeys]="['catalog.marketplace.help.filters.bullets.search']" />
              </summary>
              <div class="filter-grid">
                <label>
                  <span>{{ t('catalog.marketplace.filters.searchSettlements') }}</span>
                  <input pInputText [value]="settlementSearch()" (input)="onSettlementSearch($event)" />
                </label>
              </div>
            </details>
            @if (filteredSettlements().length === 0) {
              <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
            } @else {
              <p-table
                [value]="filteredSettlements()"
                dataKey="id"
                [paginator]="true"
                [rows]="10"
                [selection]="selectedSettlements()"
                (selectionChange)="onSettlementSelectionChange($event)">
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width:44px"><p-tableHeaderCheckbox /></th>
                    <th><button type="button" class="sort-btn" [class.active]="settlementSortField() === 'orderReferenceId'" (click)="sortSettlementsBy('orderReferenceId')">{{ t('catalog.marketplace.dashboard.order') }}</button></th>
                    <th><button type="button" class="sort-btn" [class.active]="settlementSortField() === 'netPayoutAmount'" (click)="sortSettlementsBy('netPayoutAmount')">{{ t('catalog.marketplace.dashboard.netPayout') }}</button></th>
                    <th><button type="button" class="sort-btn" [class.active]="settlementSortField() === 'currencyCode'" (click)="sortSettlementsBy('currencyCode')">{{ t('catalog.marketplace.dashboard.currency') }}</button></th>
                    <th><button type="button" class="sort-btn" [class.active]="settlementSortField() === 'createdAtUtc'" (click)="sortSettlementsBy('createdAtUtc')">{{ t('catalog.marketplace.dashboard.created') }}</button></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td><p-tableCheckbox [value]="row" /></td>
                    <td>{{ row.orderReferenceId }}</td>
                    <td>{{ row.netPayoutAmount | number:'1.2-2' }}</td>
                    <td>{{ row.currencyCode }}</td>
                    <td>{{ row.createdAtUtc | date:'medium' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
            @if (settlementInfo()) { <p class="info">{{ settlementInfo() }}</p> }
          </section>

          <section class="card list-panel">
            <h3>{{ t('catalog.marketplace.dashboard.openDisputesList') }}</h3>
            <div class="toolbar">
              <button pButton type="button" [label]="t('catalog.marketplace.ops.openDispute')" (click)="openDisputeDialog()"></button>
              <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.refresh')" (click)="load()"></button>
              <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.bulk')" (click)="applyDisputeBulk()"></button>
              <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.export')" (click)="exportDisputes()"></button>
              <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.open')" [disabled]="selectedDisputes().length !== 1" (click)="openSelectedDispute()"></button>
            </div>
            <details class="filters" [open]="true">
              <summary>
                <span>{{ t('catalog.marketplace.filters.title') }}</span>
                <ain-info-hint
                  [titleKey]="'catalog.marketplace.help.filters.title'"
                  [descriptionKey]="'catalog.marketplace.help.filters.description'"
                  [bulletKeys]="[
                    'catalog.marketplace.help.filters.bullets.search',
                    'catalog.marketplace.help.filters.bullets.status'
                  ]" />
              </summary>
              <div class="filter-grid">
                <label>
                  <span>{{ t('catalog.marketplace.filters.searchDisputes') }}</span>
                  <input pInputText [value]="disputeSearch()" (input)="onDisputeSearch($event)" />
                </label>
                <label>
                  <span>{{ t('catalog.marketplace.filters.status') }}</span>
                  <p-select
                    [options]="disputeStatusOptions"
                    optionValue="value"
                    [(ngModel)]="disputeStatusFilter"
                    [showClear]="true"
                    appendTo="body">
                    <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                    <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                  </p-select>
                </label>
              </div>
            </details>
            @if (filteredDisputes().length === 0) {
              <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
            } @else {
              <p-table
                [value]="filteredDisputes()"
                dataKey="id"
                [paginator]="true"
                [rows]="10"
                [selection]="selectedDisputes()"
                (selectionChange)="onDisputeSelectionChange($event)">
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width:44px"><p-tableHeaderCheckbox /></th>
                    <th><button type="button" class="sort-btn" [class.active]="disputeSortField() === 'orderReferenceId'" (click)="sortDisputesBy('orderReferenceId')">{{ t('catalog.marketplace.dashboard.order') }}</button></th>
                    <th><button type="button" class="sort-btn" [class.active]="disputeSortField() === 'status'" (click)="sortDisputesBy('status')">{{ t('columns.status') }}</button></th>
                    <th><button type="button" class="sort-btn" [class.active]="disputeSortField() === 'disputedAmount'" (click)="sortDisputesBy('disputedAmount')">{{ t('catalog.marketplace.dashboard.amount') }}</button></th>
                    <th>{{ t('catalog.marketplace.dashboard.dualApproval') }}</th>
                    <th><button type="button" class="sort-btn" [class.active]="disputeSortField() === 'reason'" (click)="sortDisputesBy('reason')">{{ t('catalog.marketplace.dashboard.reason') }}</button></th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td><p-tableCheckbox [value]="row" /></td>
                    <td>{{ row.orderReferenceId }}</td>
                    <td>{{ t(disputeStatusKey(row.status)) }}</td>
                    <td>{{ row.disputedAmount | number:'1.2-2' }}</td>
                    <td>{{ row.dualApprovalRequired ? t('catalog.marketplace.dashboard.dualApprovalRequired') : '—' }}</td>
                    <td>{{ row.reason }}</td>
                    <td class="actions">
                      @if (row.status === 'Open') {
                        <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.marketplace.disputes.review')" [disabled]="acting()" (click)="reviewDispute(row)"></button>
                      }
                      @if (row.status === 'Open' || row.status === 'UnderReview') {
                        <button pButton type="button" size="small" [label]="t('catalog.marketplace.disputes.resolve')" [disabled]="acting()" (click)="openResolve(row)"></button>
                      }
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
            @if (disputeInfo()) { <p class="info">{{ disputeInfo() }}</p> }
          </section>
        </section>
      }

      <p-dialog [header]="t('catalog.marketplace.ops.escrowHold')" [(visible)]="escrowVisible" [modal]="true" [style]="{ width: '420px' }">
        <div class="form">
          <label>{{ t('catalog.marketplace.ops.orderId') }}</label>
          <ain-commerce-order-selector [(ngModel)]="orderReferenceId" />
          <label>{{ t('catalog.marketplace.ops.amount') }}</label>
          <input pInputText type="number" [(ngModel)]="escrowAmount" class="full" />
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="escrowVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.marketplace.ops.escrowHold')" [disabled]="acting()" (click)="submitEscrowHold()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.marketplace.ops.settle')" [(visible)]="settleVisible" [modal]="true" [style]="{ width: '460px' }">
        <div class="form">
          <label>{{ t('catalog.marketplace.ops.orderId') }}</label>
          <ain-commerce-order-selector [(ngModel)]="orderReferenceId" />
          <label>{{ t('catalog.marketplace.ops.seller') }}</label>
          <ain-marketplace-seller-selector [(ngModel)]="settleSellerId" [approvedOnly]="true" />
          <label>{{ t('catalog.marketplace.ops.gross') }}</label>
          <input pInputText type="number" [(ngModel)]="settleGross" class="full" />
          <label>{{ t('catalog.marketplace.ops.commission') }}</label>
          <input pInputText type="number" [(ngModel)]="settleCommission" class="full" />
          <label>{{ t('catalog.marketplace.ops.idempotency') }}</label>
          <input pInputText [(ngModel)]="settleIdempotencyKey" class="full" />
          <div class="check-row">
            <p-checkbox [(ngModel)]="settleRequireEscrowRelease" [binary]="true" inputId="escRel" />
            <label for="escRel">{{ t('catalog.marketplace.ops.requireEscrowRelease') }}</label>
          </div>
          <div class="check-row">
            <p-checkbox [(ngModel)]="settlePostFinancial" [binary]="true" inputId="postSettle" />
            <label for="postSettle">{{ t('catalog.marketplace.disputes.postFinancial') }}</label>
          </div>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="settleVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.marketplace.ops.settle')" [disabled]="acting()" (click)="submitSettle()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.marketplace.ops.openDispute')" [(visible)]="disputeVisible" [modal]="true" [style]="{ width: '460px' }">
        <div class="form">
          <label>{{ t('catalog.marketplace.ops.orderId') }}</label>
          <ain-commerce-order-selector [(ngModel)]="orderReferenceId" />
          <label>{{ t('catalog.marketplace.ops.seller') }}</label>
          <ain-marketplace-seller-selector [(ngModel)]="disputeSellerId" [approvedOnly]="true" />
          <label>{{ t('catalog.marketplace.dashboard.amount') }}</label>
          <input pInputText type="number" [(ngModel)]="disputeAmount" class="full" />
          <label>{{ t('catalog.marketplace.dashboard.reason') }}</label>
          <textarea pTextarea [(ngModel)]="disputeReason" rows="2" class="full"></textarea>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="disputeVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.marketplace.ops.openDispute')" [disabled]="acting()" (click)="submitOpenDispute()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('catalog.marketplace.disputes.review')"
        [(visible)]="reviewVisible"
        [modal]="true"
        [style]="{ width: '440px' }">
        <div class="form">
          <label>{{ t('catalog.marketplace.dashboard.reason') }}</label>
          <textarea pTextarea [(ngModel)]="reviewReason" rows="3" class="full"></textarea>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="reviewVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.marketplace.disputes.review')" [disabled]="acting()" (click)="confirmReview()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('catalog.marketplace.disputes.resolveTitle')"
        [(visible)]="resolveVisible"
        [modal]="true"
        [style]="{ width: '480px' }">
        @if (selectedDispute()) {
          <div class="form">
            <label>{{ t('catalog.marketplace.disputes.outcome') }}</label>
            <p-select
              [options]="outcomeOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="resolveFavorBuyer"
              [style]="{ width: '100%' }" />
            <label>{{ t('catalog.marketplace.disputes.firstApprover') }}</label>
            <ain-customer-selector [(ngModel)]="resolveFirstApproverId" />
            @if (selectedDispute()!.dualApprovalRequired) {
              <label>{{ t('catalog.marketplace.disputes.secondApprover') }}</label>
              <ain-customer-selector [(ngModel)]="resolveSecondApproverId" />
            }
            <label>{{ t('catalog.marketplace.disputes.resolveReason') }}</label>
            <textarea pTextarea [(ngModel)]="resolveReason" rows="3" class="full"></textarea>
            <label>{{ t('catalog.marketplace.disputes.notes') }}</label>
            <textarea pTextarea [(ngModel)]="resolveNotes" rows="2" class="full"></textarea>
            <div class="check-row">
              <p-checkbox [(ngModel)]="resolvePostFinancial" [binary]="true" inputId="postFin" />
              <label for="postFin">{{ t('catalog.marketplace.disputes.postFinancial') }}</label>
            </div>
          </div>
        }
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="resolveVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.marketplace.disputes.confirmResolve')" [disabled]="acting()" (click)="confirmResolve()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 16px; }
    .stats { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px; }
    .stat { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; display: grid; gap: 4px; }
    .label { font-size: 0.8rem; color: #6b7280; }
    .value { font-size: 1.25rem; font-weight: 700; }
    h3 { margin: 0; font-size: 1rem; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .list-panel { display: grid; gap: 10px; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .form { display: grid; gap: 8px; }
    .form label { font-size: 0.85rem; font-weight: 600; }
    .full { width: 100%; }
    .check-row { display: flex; align-items: center; gap: 8px; }
    ${MARKETPLACE_LIST_TOOLBAR_STYLES}
  `]
})
export class MarketplaceDashboardComponent implements OnInit, OnDestroy {
  private readonly service = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private liveSubscription: MarketplaceLiveSubscription = { topic: null, eventsSubscription: null };

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly dashboard = signal<MarketplaceDashboard | null>(null);
  readonly settlements = signal<MarketplaceSettlementSummary[]>([]);
  readonly disputes = signal<MarketplaceDispute[]>([]);
  readonly selectedDispute = signal<MarketplaceDispute | null>(null);
  readonly settlementInfo = signal('');
  readonly disputeInfo = signal('');
  readonly selectedSettlements = signal<MarketplaceSettlementSummary[]>([]);
  readonly selectedDisputes = signal<MarketplaceDispute[]>([]);
  readonly settlementSearch = signal('');
  readonly disputeSearch = signal('');
  readonly settlementSortField = signal<SettlementSortField>('createdAtUtc');
  readonly settlementSortOrder = signal<1 | -1>(-1);
  readonly disputeSortField = signal<DisputeSortField>('orderReferenceId');
  readonly disputeSortOrder = signal<1 | -1>(1);

  liveStatusKey = liveStatusKey;

  escrowVisible = false;
  settleVisible = false;
  disputeVisible = false;
  orderReferenceId: string | null = null;
  escrowAmount = 500;
  settleSellerId: string | null = null;
  settleGross = 500;
  settleCommission = 50;
  settleIdempotencyKey = '';
  settleRequireEscrowRelease = true;
  settlePostFinancial = false;
  disputeSellerId: string | null = null;
  disputeAmount = 1500;
  disputeReason = '';
  disputeStatusFilter: string | null = null;

  reviewVisible = false;
  reviewReason = '';
  private reviewTarget: MarketplaceDispute | null = null;

  resolveVisible = false;
  resolveFavorBuyer = true;
  resolveFirstApproverId: string | null = null;
  resolveSecondApproverId: string | null = null;
  resolveReason = '';
  resolveNotes = '';
  resolvePostFinancial = false;

  readonly outcomeOptions = [
    { label: '', value: true },
    { label: '', value: false }
  ];

  readonly disputeStatusOptions = [
    { value: 'Open', labelKey: 'catalog.marketplace.disputes.status.open' },
    { value: 'UnderReview', labelKey: 'catalog.marketplace.disputes.status.underReview' },
    { value: 'Resolved', labelKey: 'catalog.marketplace.disputes.status.resolved' }
  ];

  ngOnInit(): void {
    this.outcomeOptions[0].label = this.t('catalog.marketplace.disputes.favorBuyer');
    this.outcomeOptions[1].label = this.t('catalog.marketplace.disputes.favorSeller');
    this.settlementSearch.set(this.route.snapshot.queryParamMap.get('qSettlements') ?? '');
    this.disputeSearch.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void unsubscribeMarketplaceOperationsLiveUpdates(this.liveUpdateService, this.liveSubscription);
  }

  load(showLoading = true): void {
    if (showLoading) {
      this.loading.set(true);
    }

    this.settlementInfo.set('');
    this.disputeInfo.set('');
    this.service.getMarketplaceDashboard().subscribe({
      next: d => {
        this.dashboard.set(d);
        this.settlements.set(d.recentSettlements ?? []);
        this.disputes.set(d.openDisputes ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.dashboard.set(null);
        this.settlements.set([]);
        this.disputes.set([]);
        this.loading.set(false);
      }
    });
  }

  filteredSettlements(): MarketplaceSettlementSummary[] {
    const value = this.settlementSearch().trim().toLowerCase();
    const filtered = !value
      ? this.settlements()
      : this.settlements().filter(row =>
        row.orderReferenceId.toLowerCase().includes(value)
        || row.currencyCode.toLowerCase().includes(value));

    const field = this.settlementSortField();
    const order = this.settlementSortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field]) * order);
  }

  filteredDisputes(): MarketplaceDispute[] {
    const value = this.disputeSearch().trim().toLowerCase();
    const status = this.disputeStatusFilter;
    const filtered = this.disputes().filter(row => {
      if (status && row.status !== status) {
        return false;
      }

      if (!value) {
        return true;
      }

      return row.orderReferenceId.toLowerCase().includes(value)
        || row.reason.toLowerCase().includes(value)
        || row.status.toLowerCase().includes(value);
    });

    const field = this.disputeSortField();
    const order = this.disputeSortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field]) * order);
  }

  onSettlementSearch(event: Event): void {
    this.settlementSearch.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { qSettlements: this.settlementSearch().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onDisputeSearch(event: Event): void {
    this.disputeSearch.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.disputeSearch().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSettlementSelectionChange(selection: MarketplaceSettlementSummary[] | MarketplaceSettlementSummary | null): void {
    this.selectedSettlements.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  onDisputeSelectionChange(selection: MarketplaceDispute[] | MarketplaceDispute | null): void {
    this.selectedDisputes.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  applySettlementBulk(): void {
    if (this.selectedSettlements().length === 0) {
      this.settlementInfo.set(this.t('catalog.marketplace.actions.bulkNothingSelected'));
      return;
    }

    this.settlementInfo.set(this.t('catalog.marketplace.actions.bulkQueued'));
  }

  applyDisputeBulk(): void {
    if (this.selectedDisputes().length === 0) {
      this.disputeInfo.set(this.t('catalog.marketplace.actions.bulkNothingSelected'));
      return;
    }

    this.disputeInfo.set(this.t('catalog.marketplace.actions.bulkQueued'));
  }

  exportSettlements(): void {
    const rows = this.filteredSettlements();
    if (rows.length === 0) {
      this.settlementInfo.set(this.t('catalog.marketplace.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'marketplace-settlements.csv',
      ['OrderReferenceId', 'NetPayoutAmount', 'CurrencyCode', 'CreatedAtUtc'],
      rows.map(row => [row.orderReferenceId, row.netPayoutAmount.toString(), row.currencyCode, row.createdAtUtc]));
    this.settlementInfo.set(this.t('catalog.marketplace.actions.exportReady'));
  }

  exportDisputes(): void {
    const rows = this.filteredDisputes();
    if (rows.length === 0) {
      this.disputeInfo.set(this.t('catalog.marketplace.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'marketplace-disputes.csv',
      ['OrderReferenceId', 'Status', 'DisputedAmount', 'Reason', 'DualApprovalRequired'],
      rows.map(row => [
        row.orderReferenceId,
        row.status,
        row.disputedAmount.toString(),
        row.reason,
        row.dualApprovalRequired ? 'Yes' : 'No'
      ]));
    this.disputeInfo.set(this.t('catalog.marketplace.actions.exportReady'));
  }

  openSelectedSettlement(): void {
    const row = this.selectedSettlements()[0];
    if (!row) {
      return;
    }

    this.settlementInfo.set(`${row.orderReferenceId} — ${row.netPayoutAmount} ${row.currencyCode}`);
  }

  openSelectedDispute(): void {
    const row = this.selectedDisputes()[0];
    if (!row) {
      return;
    }

    if (row.status === 'Open' || row.status === 'UnderReview') {
      this.openResolve(row);
      return;
    }

    this.disputeInfo.set(`${row.orderReferenceId} — ${this.t(this.disputeStatusKey(row.status))}`);
  }

  sortSettlementsBy(field: SettlementSortField): void {
    toggleSortField(this.settlementSortField(), field, this.settlementSortOrder(), v => this.settlementSortField.set(v), v => this.settlementSortOrder.set(v));
  }

  sortDisputesBy(field: DisputeSortField): void {
    toggleSortField(this.disputeSortField(), field, this.disputeSortOrder(), v => this.disputeSortField.set(v), v => this.disputeSortOrder.set(v));
  }

  reviewDispute(row: MarketplaceDispute): void {
    if (row.dualApprovalRequired) {
      this.reviewTarget = row;
      this.reviewReason = '';
      this.reviewVisible = true;
      return;
    }

    this.submitReview(row);
  }

  confirmReview(): void {
    if (!this.reviewTarget) {
      return;
    }

    if (!this.reviewReason.trim()) {
      this.disputeInfo.set(this.t('catalog.marketplace.disputes.resolveReasonRequired'));
      return;
    }

    this.submitReview(this.reviewTarget);
  }

  private submitReview(row: MarketplaceDispute): void {
    this.acting.set(true);
    this.service.reviewMarketplaceDispute(row.id).subscribe({
      next: () => {
        this.reviewVisible = false;
        this.reviewTarget = null;
        this.disputeInfo.set(this.t('catalog.marketplace.disputes.reviewed'));
        this.load(false);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openResolve(row: MarketplaceDispute): void {
    this.selectedDispute.set(row);
    this.resolveFavorBuyer = true;
    this.resolveFirstApproverId = null;
    this.resolveSecondApproverId = null;
    this.resolveReason = '';
    this.resolveNotes = '';
    this.resolvePostFinancial = false;
    this.resolveVisible = true;
  }

  confirmResolve(): void {
    const dispute = this.selectedDispute();
    if (!dispute) {
      return;
    }

    if (!this.resolveReason.trim()) {
      this.disputeInfo.set(this.t('catalog.marketplace.disputes.resolveReasonRequired'));
      return;
    }

    if (!this.resolveFirstApproverId) {
      this.disputeInfo.set(this.t('catalog.customer.required'));
      return;
    }

    if (dispute.dualApprovalRequired && !this.resolveSecondApproverId) {
      this.disputeInfo.set(this.t('catalog.customer.required'));
      return;
    }

    const notes = this.resolveNotes.trim()
      ? `${this.resolveReason.trim()}\n\n${this.resolveNotes.trim()}`
      : this.resolveReason.trim();

    this.acting.set(true);
    this.service.resolveMarketplaceDispute(dispute.id, {
      favorBuyer: this.resolveFavorBuyer,
      firstApproverReference: this.resolveFirstApproverId,
      secondApproverReference: dispute.dualApprovalRequired ? this.resolveSecondApproverId : null,
      resolutionNotes: notes,
      postFinancialAdjustment: this.resolvePostFinancial
    }).subscribe({
      next: () => {
        this.resolveVisible = false;
        this.disputeInfo.set(this.t('catalog.marketplace.disputes.resolved'));
        this.load(false);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  disputeStatusKey(status: string): string {
    return `catalog.marketplace.disputes.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  openEscrowDialog(): void {
    this.orderReferenceId = null;
    this.escrowAmount = 500;
    this.escrowVisible = true;
  }

  submitEscrowHold(): void {
    const orderReferenceId = this.orderReferenceId?.trim();
    if (!orderReferenceId || this.escrowAmount <= 0) {
      return;
    }

    this.acting.set(true);
    this.service.holdMarketplaceEscrow(orderReferenceId, this.escrowAmount).subscribe({
      next: () => {
        this.escrowVisible = false;
        this.settlementInfo.set(this.t('catalog.marketplace.ops.escrowHeld'));
        this.load(false);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openSettleDialog(): void {
    this.orderReferenceId = null;
    this.settleSellerId = null;
    this.settleGross = 500;
    this.settleCommission = 50;
    this.settleIdempotencyKey = `settle-${Date.now()}`;
    this.settleRequireEscrowRelease = true;
    this.settlePostFinancial = false;
    this.settleVisible = true;
  }

  submitSettle(): void {
    const orderReferenceId = this.orderReferenceId?.trim();
    if (!orderReferenceId || !this.settleSellerId || !this.settleIdempotencyKey.trim()) {
      return;
    }

    this.acting.set(true);
    this.service.settleMarketplaceOrder(orderReferenceId, {
      sellerId: this.settleSellerId,
      grossAmount: this.settleGross,
      commissionAmount: this.settleCommission,
      idempotencyKey: this.settleIdempotencyKey.trim(),
      postFinancialEntry: this.settlePostFinancial,
      requireEscrowRelease: this.settleRequireEscrowRelease
    }).subscribe({
      next: () => {
        this.settleVisible = false;
        this.settlementInfo.set(this.t('catalog.marketplace.ops.settled'));
        this.load(false);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openDisputeDialog(): void {
    this.orderReferenceId = null;
    this.disputeSellerId = null;
    this.disputeAmount = 1500;
    this.disputeReason = '';
    this.disputeVisible = true;
  }

  submitOpenDispute(): void {
    const orderReferenceId = this.orderReferenceId?.trim();
    if (!orderReferenceId || !this.disputeSellerId || !this.disputeReason.trim()) {
      return;
    }

    this.acting.set(true);
    this.service.openMarketplaceDispute({
      orderReferenceId,
      sellerId: this.disputeSellerId,
      reason: this.disputeReason.trim(),
      disputedAmount: this.disputeAmount
    }).subscribe({
      next: () => {
        this.disputeVisible = false;
        this.disputeInfo.set(this.t('catalog.marketplace.ops.disputeOpened'));
        this.load(false);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  goSellers(): void {
    void this.router.navigate(['/marketplace/sellers']);
  }

  goCommissionRules(): void {
    void this.router.navigate(['/marketplace/commission-rules']);
  }

  goSellerTiers(): void {
    void this.router.navigate(['/marketplace/seller-tiers']);
  }

  goRetailMedia(): void {
    void this.router.navigate(['/marketplace/retail-media']);
  }

  private async startLiveUpdates(): Promise<void> {
    this.liveSubscription = await subscribeMarketplaceOperationsLiveUpdates(
      this.contextService.context()?.tenantId,
      this.liveUpdateService,
      () => this.load(false));
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
